import { useState, useEffect, useRef, useCallback } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { getRecoil } from 'recoil-nexus';
import organizationIdSelector from '../atoms/organizationIdSelector';
import userDataState from '../atoms/userDataAtom';
import { consola } from 'consola';
import { ChatMessage, ChatUser } from '../types/chat';

interface UseInfiniteMessagesProps {
  groupId: string;
  pageSize?: number;
  filter?: string | null;
}

interface UseInfiniteMessagesReturn {
  messages: ChatMessage[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  markAsViewed: (messageId: string) => Promise<void>;
  refreshMessages: () => void;
}

/**
 * Enhanced hook for infinite scrolling chat messages with real-time updates.
 * Supports pagination, real-time subscriptions, and optimistic updates.
 */
export const useInfiniteMessages = ({
  groupId,
  pageSize = 30,
  filter = null,
}: UseInfiniteMessagesProps): UseInfiniteMessagesReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState<firebase.firestore.DocumentSnapshot | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);
  const subscribedGroupRef = useRef<string | null>(null);

  const organizationId = getRecoil(organizationIdSelector);
  const userData = getRecoil<any>(userDataState);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      subscribedGroupRef.current = null;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  // Transform Firestore document to ChatMessage
  const transformFirestoreMessage = useCallback((doc: firebase.firestore.DocumentSnapshot): ChatMessage => {
    const data = doc.data();
    if (!data) throw new Error('Message document has no data');

    return {
      id: doc.id,
      messageType: data.messageType || 'text',
      fromUID: data.fromUID,
      fromUser: data.fromUser as ChatUser,
      groupId: data.groupId,
      appDestination: data.appDestination || 'messaging',
      message: data.message || '',
      createdOn: data.createdOn?.toDate() || new Date(),
      processed: data.processed,
      processedOn: data.processedOn?.toDate(),
      viewedBy: data.viewedBy || [],
      audioUrl: data.audioUrl,
      imageUrl: data.imageUrl,
      videoUrl: data.videoUrl,
      videoWidth: data.videoWidth,
      videoHeight: data.videoHeight,
      pdfUrl: data.pdfUrl,
      fileUrl: data.fileUrl,
      fileName: data.fileName,
      fileSize: data.fileSize,
      duration: data.duration,
      metadata: data.metadata,
      tokenized: data.tokenized,
      keywords: data.keywords,
      seenBy: data.seenBy,
      isUploading: data.isUploading,
      uploadProgress: data.uploadProgress,
      reactions: data.reactions || [],
      replyTo: data.replyTo,
    };
  }, []);

  // Build Firestore query with optional keyword filtering
  const buildQuery = useCallback((isLoadMore = false) => {
    if (!organizationId || !groupId) return null;

    const baseRef = firebase
      .firestore()
      .collection('organizations')
      .doc(organizationId)
      .collection('messageGroups')
      .doc(groupId)
      .collection('messages');

    let query: firebase.firestore.Query;

    if (filter && filter.length > 0) {
      const filterArray = filter.split(' ').filter(term => term.length > 0);
      query = baseRef
        .where('keywords', 'array-contains-any', filterArray)
        .orderBy('createdOn', 'desc');
    } else {
      query = baseRef.orderBy('createdOn', 'desc');
    }

    if (isLoadMore && lastVisible) {
      query = query.startAfter(lastVisible);
    }

    return query.limit(pageSize);
  }, [organizationId, groupId, filter, lastVisible, pageSize]);

  // Load initial messages with real-time subscription
  const loadInitialMessages = useCallback(async () => {
    const currentGroupKey = `${organizationId}-${groupId}`;

    if (!groupId || !organizationId || loadingRef.current || subscribedGroupRef.current === currentGroupKey) {
      return;
    }

    try {
      setLoading(true);
      loadingRef.current = true;
      subscribedGroupRef.current = currentGroupKey;

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      const baseRef = firebase
        .firestore()
        .collection('organizations')
        .doc(organizationId)
        .collection('messageGroups')
        .doc(groupId)
        .collection('messages');

      let query: firebase.firestore.Query;
      if (filter && filter.length > 0) {
        const filterArray = filter.split(' ').filter(term => term.length > 0);
        query = baseRef
          .where('keywords', 'array-contains-any', filterArray)
          .orderBy('createdOn', 'desc');
      } else {
        query = baseRef.orderBy('createdOn', 'desc');
      }

      query = query.limit(pageSize);

      consola.debug('++++++ SNAPSHOT -> useInfiniteMessages: subscribing', currentGroupKey);

      unsubscribeRef.current = query.onSnapshot(
        (snapshot) => {
          if (!mountedRef.current) return;

          consola.debug('++++++ SNAPSHOT -> useInfiniteMessages: update received');

          const newMessages: ChatMessage[] = [];
          let newLastVisible: firebase.firestore.DocumentSnapshot | null = null;

          snapshot.docs.forEach((doc) => {
            try {
              const message = transformFirestoreMessage(doc);
              newMessages.push(message);
              newLastVisible = doc;
            } catch (error) {
              consola.error('useInfiniteMessages: failed to transform message', {
                messageId: doc.id,
                error,
              });
            }
          });

          // Reverse to show newest at bottom
          newMessages.reverse();

          setMessages(newMessages);
          setLastVisible(newLastVisible);
          setHasMore(snapshot.docs.length === pageSize);
          setLoading(false);
          loadingRef.current = false;
        },
        (error) => {
          consola.error('useInfiniteMessages: Firestore subscription failed', {
            groupKey: currentGroupKey,
            error,
          });
          setLoading(false);
          loadingRef.current = false;
          subscribedGroupRef.current = null;
        }
      );
    } catch (error) {
      consola.error('useInfiniteMessages: loadInitialMessages failed', { error });
      setLoading(false);
      loadingRef.current = false;
      subscribedGroupRef.current = null;
    }
  }, [groupId, organizationId, filter, pageSize, transformFirestoreMessage]);

  // Load more messages (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || loadingRef.current || !lastVisible) return;

    try {
      loadingRef.current = true;
      setLoading(true);

      const query = buildQuery(true);
      if (!query) {
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      consola.debug('++++++ READ -> useInfiniteMessages: load more');
      const snapshot = await query.get();

      if (!mountedRef.current) return;

      const olderMessages: ChatMessage[] = [];
      let newLastVisible: firebase.firestore.DocumentSnapshot | null = null;

      snapshot.docs.forEach((doc) => {
        try {
          const message = transformFirestoreMessage(doc);
          olderMessages.push(message);
          newLastVisible = doc;
        } catch (error) {
          consola.error('useInfiniteMessages: failed to transform loadMore message', {
            messageId: doc.id,
            error,
          });
        }
      });

      olderMessages.reverse();

      setMessages(prevMessages => [...olderMessages, ...prevMessages]);
      setLastVisible(newLastVisible);
      setHasMore(snapshot.docs.length === pageSize);
    } catch (error) {
      consola.error('useInfiniteMessages: loadMore failed', { error });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [hasMore, loading, lastVisible, buildQuery, transformFirestoreMessage, pageSize]);

  // Send a new message with optimistic UI update
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || !groupId || !organizationId || !userData) {
      throw new Error('Missing required data for sending message');
    }

    const optimisticMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      messageType: 'text',
      fromUID: userData.uid,
      fromUser: userData as ChatUser,
      groupId,
      appDestination: 'messaging',
      message: text.trim(),
      createdOn: new Date(),
      isUploading: true,
    };

    setMessages(prevMessages => [...prevMessages, optimisticMessage]);

    try {
      const { sendMessageToGroup } = await import('../data/MessageData');
      await sendMessageToGroup(groupId, text.trim());

      // Real message arrives via subscription; remove the optimistic placeholder
      setMessages(prevMessages =>
        prevMessages.filter(msg => msg.id !== optimisticMessage.id)
      );
    } catch (error) {
      consola.error('useInfiniteMessages: sendMessage failed', { groupId, error });
      setMessages(prevMessages =>
        prevMessages.filter(msg => !msg.id.startsWith('temp-'))
      );
      throw error;
    }
  }, [groupId, organizationId, userData]);

  // Mark a single message as viewed
  const markAsViewed = useCallback(async (messageId: string) => {
    if (!userData?.uid || !groupId || !organizationId) return;

    try {
      const { updateMessageViewedBy } = await import('../data/MessageData');
      await updateMessageViewedBy({ userId: userData.uid, groupId, messageId });
    } catch (error) {
      consola.error('useInfiniteMessages: markAsViewed failed', { messageId, error });
    }
  }, [userData?.uid, groupId, organizationId]);

  // Refresh — tear down and reload from the beginning
  const refreshMessages = useCallback(() => {
    setMessages([]);
    setLastVisible(null);
    setHasMore(true);
    subscribedGroupRef.current = null;
    loadInitialMessages();
  }, [loadInitialMessages]);

  // Subscribe whenever groupId, organizationId, or filter changes
  useEffect(() => {
    if (groupId && organizationId) {
      subscribedGroupRef.current = null;
      loadInitialMessages();
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      subscribedGroupRef.current = null;
    };
  }, [groupId, organizationId, filter]); // loadInitialMessages intentionally excluded to avoid double-subscribe

  return {
    messages,
    loading,
    hasMore,
    loadMore,
    sendMessage,
    markAsViewed,
    refreshMessages,
  };
};
